import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, formatApiError } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { localizedPath } from '../../i18n/routes';
import { Eye, EyeSlash, ArrowRight, EnvelopeSimple, ShieldCheck, Vault, LockKey } from '@phosphor-icons/react';
import axios from 'axios';
import { useSiteContent, cmsVal } from '../../hooks/useSiteContent';
import { CmsEditable } from '../../components/cms/CmsEditable';
import SEOHead from '../../components/SEOHead';
import WMLogo from '../../components/brand/WMLogo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { login, verifyMfa } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { content: cms } = useSiteContent('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // APEX M2 — connexion en 2 étapes : /auth/login renvoie un jeton
  // intermédiaire `mfa_pending` (aucun JWT avant le second facteur), puis
  // /auth/mfa/verify émet la session. `useRecovery` bascule la saisie sur un
  // code de récupération à usage unique.
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [showActivationRequest, setShowActivationRequest] = useState(false);
  const [activationEmail, setActivationEmail] = useState('');
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationSent, setActivationSent] = useState(false);

  const from = location.state?.from?.pathname || '/portal';

  // ─── Bug fix Safari/iCloud Keychain (15/05/2026) ─────────────────────────────
  // Symptôme remonté côté clients Safari (www.wealth-management-assets.com) :
  // clic sur « Connecter » → la page se rafraîchit, champs vidés, AUCUN message
  // d'erreur. C'est la signature d'une submission native non bloquée :
  //   1. Safari iCloud Keychain autofill peut déclencher un submit "trusted"
  //      qui bypasse le handler React onSubmit si le formulaire n'a pas les
  //      bons attributs (name/autoComplete absents → le navigateur ne reconnaît
  //      pas le form comme un "credentials form" géré par l'app).
  //   2. Sur connexions lentes, le submit peut racer l'hydratation React.
  //
  // Fix défensif multicouche :
  //   (a) name + autoComplete sur les inputs (cause principale)
  //   (b) listener natif `submit` en phase capture sur le DOM brut → garantit
  //       que preventDefault s'exécute AVANT que Safari ne déclenche la
  //       navigation native, indépendamment du synthetic event de React.
  const formRef = useRef(null);
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const nativeGuard = (e) => {
      // Bloque inconditionnellement la submission native — le handler React
      // (onSubmit) se charge ensuite du flux applicatif.
      e.preventDefault();
    };
    form.addEventListener('submit', nativeGuard, { capture: true });
    return () => form.removeEventListener('submit', nativeGuard, { capture: true });
  }, []);

  const navigateByRole = (user) => {
    if (user.role === 'super_admin' || user.role === 'admin') {
      navigate('/admin');
    } else if (user.role === 'sponsor') {
      navigate('/sponsor');
    } else {
      navigate(from);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // APEX M2 — Étape 2 : vérification du second facteur (TOTP ou code de
    // récupération) contre le jeton intermédiaire.
    if (mfaRequired && mfaToken) {
      try {
        const user = await verifyMfa(mfaToken, mfaCode.trim(), { recovery: useRecovery });
        if (user.force_password_reset && user.reset_token) {
          navigate(`/reset-password?token=${user.reset_token}`);
          return;
        }
        navigateByRole(user);
        return;
      } catch (err) {
        const status = err?.response?.status;
        const detail = String(err?.response?.data?.detail || '');
        // Défi expiré ou jeton invalide → retour à l'étape 1.
        if (status === 401 && /expiré|invalide.*jeton|jeton.*invalide/i.test(detail) && /défi|jeton/i.test(detail)) {
          setMfaRequired(false);
          setMfaToken('');
          setMfaCode('');
          setUseRecovery(false);
        }
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const user = await login(email, password);
      if (user.mfa_required) {
        setMfaRequired(true);
        setMfaToken(user.mfa_token || '');
        setMfaCode('');
        setLoading(false);
        return;
      }
      if (user.mfa_setup_required) {
        // APEX M2 — enrôlement guidé (admins sans MFA + investisseurs à MFA
        // imposée). Le jeton mfa_setup est aussi passé en state pour le
        // fallback Safari ITP (cookies cross-site bloqués).
        navigate('/mfa-enrollment', {
          state: {
            mfaToken: user.mfa_token || '',
            imposed: !!user.enrollment_imposed_by_admin,
          },
        });
        return;
      }
      if (user.force_password_reset && user.reset_token) {
        navigate(`/reset-password?token=${user.reset_token}`);
        return;
      }
      navigateByRole(user);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      // LOT 482 — Detect "Account not activated" via the new error_code in the API response
      // (works for both old text-only response and new structured response)
      const errorCode = err?.response?.data?.detail?.error_code;
      const looksLikeNotActivated =
        errorCode === 'ACCOUNT_NOT_ACTIVATED' ||
        msg.toLowerCase().includes('not activated') ||
        msg.toLowerCase().includes('activation') ||
        msg.toLowerCase().includes('pas encore activé') ||
        msg.toLowerCase().includes("n'est pas encore activé");
      if (looksLikeNotActivated) {
        setShowActivationRequest(true);
        setActivationEmail(email);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivationRequest = async (e) => {
    e.preventDefault();
    setActivationLoading(true);
    try {
      await axios.post(`${API}/api/auth/request-activation`, { email: activationEmail });
      setActivationSent(true);
    } catch {
      setActivationSent(true);
    } finally {
      setActivationLoading(false);
    }
  };

  return (
    <div className="min-h-[90vh] flex bg-[#F7F6F2]" data-testid="login-page">
      <SEOHead
        title={lang === 'en' ? 'Sign in · Wealth Management' : 'Connexion · Wealth Management'}
        description={lang === 'en'
          ? 'Sign in to your private investor portal — Wealth Management — Global Real Assets.'
          : 'Connectez-vous à votre espace investisseur privé — Wealth Management — Global Real Assets.'}
        canonical={`https://www.wealth-management-assets.com/${lang === 'en' ? 'en/login' : 'fr/connexion'}`}
        hreflangs={{ fr: '/fr/connexion', en: '/en/login' }}
        noindex={true}
      />
      {/* ───── LEFT: FORM ───── */}
      <div className="flex-1 flex items-center justify-center py-12 px-6 md:px-10 bg-[#F7F6F2]">
        <div className="w-full max-w-md">
          {/* Logo + brand header */}
          <div className="mb-10">
            <Link to="/" className="inline-flex items-center mb-10" data-testid="login-logo" data-cms-section="brand">
              {/* LOT Public Design B (Daniel 18/06/2026) — Remplacement du
                 carré navy + "W" cheap par le WMLogo monogramme global.
                 Plus de petit "W" dans un rectangle, plus de logo ad-hoc
                 dans la page de login : on utilise la même identité visuelle
                 que le header / footer / CTA final. */}
              {/* HARDENING V1 (LOT 3) — logo compact sous sm : la version md
                  (353px incompressible) débordait de l'écran à 320-375px. */}
              <span className="hidden sm:block">
                <WMLogo variant="light" mode="horizontal" size="md" />
              </span>
              <span className="sm:hidden">
                <WMLogo variant="light" mode="horizontal" size="sm" />
              </span>
            </Link>
            <div data-cms-section="welcome">
              <p className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-gold-dark mb-4" data-testid="login-welcome-overline">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-gold-wash ring-1 ring-gold-dark/25">
                  <LockKey size={12} weight="duotone" />
                </span>
                <CmsEditable as="span" path="sections.welcome.overline"
                  value={cms?.sections?.welcome?.overline}
                  defaultValue={lang === 'fr' ? 'Espace investisseur' : 'Investor area'} />
              </p>
              <CmsEditable as="h1" path="sections.welcome.title"
                value={cms?.sections?.welcome?.title}
                defaultValue={t('login.title')}
                className="text-3xl md:text-[2rem] font-semibold tracking-[-0.02em] text-charcoal leading-[1.1] mb-3"
                testid="login-welcome-title" />
              <CmsEditable as="p" path="sections.welcome.subtitle"
                value={cms?.sections?.welcome?.subtitle}
                defaultValue={t('login.subtitle')}
                multiline
                className="text-[15px] text-graphite/80 leading-relaxed max-w-sm"
                testid="login-welcome-subtitle" />
            </div>
          </div>

          {/* Form card — rounded modern */}
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            data-testid="login-form"
            data-cms-section="form_labels"
            className="bg-white rounded-2xl p-7 md:p-8 shadow-[0_1px_2px_rgba(14,15,18,0.04),0_12px_32px_-18px_rgba(14,15,18,0.2)] border border-stone/60"
          >
            {error && (
              <div className="mb-5 p-3.5 rounded-xl bg-danger-soft border border-danger/30 text-danger text-sm flex items-start gap-2" data-testid="login-error">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-danger flex-shrink-0 mt-2" />
                {error}
              </div>
            )}

            {showActivationRequest && !activationSent && (
              <div className="mb-5 p-4 rounded-xl bg-warning-soft border border-warning/30" data-testid="activation-request-panel">
                <p className="text-sm text-warning mb-3">
                  {t('login.activationNeeded') || "Votre compte existe mais n'est pas encore activé. Pour vous connecter, vous devez d'abord définir votre mot de passe en cliquant sur le bouton « ACTIVER MON COMPTE » dans l'email que vous avez reçu. Si vous ne le retrouvez plus, demandez-en un nouveau ci-dessous (pensez aussi à vérifier votre dossier spam)."}
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={activationEmail}
                    onChange={(e) => setActivationEmail(e.target.value)}
                    className="input-premium flex-1 text-sm"
                    placeholder="votre@email.com"
                    data-testid="activation-email-input"
                  />
                  <button
                    type="button"
                    onClick={handleActivationRequest}
                    disabled={activationLoading}
                    className="btn-secondary text-sm px-4 flex items-center gap-1.5 whitespace-nowrap"
                    data-testid="activation-request-btn"
                  >
                    {activationLoading ? (
                      <span className="w-4 h-4 border-2 border-gold-dark border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <EnvelopeSimple size={16} />
                        {t('login.sendActivationLink') || 'Renvoyer le mail'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activationSent && (
              <div className="mb-5 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm" data-testid="activation-sent-msg">
                {t('login.activationLinkSent') || 'If an account with this email exists and is pending activation, an activation link has been sent. Please check your inbox.'}
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="login-email" className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-graphite/80 mb-2">{cmsVal(cms, 'sections.form_labels.email', t('login.emailLabel'))}</label>
              <input
                id="login-email"
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-premium"
                placeholder={cmsVal(cms, 'sections.form_labels.email_placeholder', 'your@email.com')}
                required
                data-testid="login-email"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="login-password" className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-graphite/80 mb-2">{cmsVal(cms, 'sections.form_labels.password', t('login.passwordLabel'))}</label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-premium pr-12"
                  placeholder={cmsVal(cms, 'sections.form_labels.password_placeholder', '••••••••')}
                  required
                  disabled={mfaRequired}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-graphite/80 hover:text-charcoal transition-colors"
                  data-testid="toggle-password"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {mfaRequired && (
              <div className="mb-5" data-testid="mfa-challenge">
                <label htmlFor="mfa-code-input" className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-graphite/80 mb-2">
                  {useRecovery
                    ? (t('login.recoveryCodeLabel') || 'Code de récupération')
                    : cmsVal(cms, 'sections.form_labels.mfa_title', t('login.mfaCodeLabel') || 'Code MFA')}
                </label>
                <input
                  id="mfa-code-input"
                  type="text"
                  name="mfa_code"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="input-premium font-mono tracking-[0.4em] indent-[0.4em] text-center text-lg"
                  placeholder={useRecovery ? 'XXXX-XXXX' : '000000'}
                  maxLength={useRecovery ? 9 : 6}
                  autoFocus
                  required
                  data-testid="mfa-code-input"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                />
                <p className="text-xs text-graphite/80 mt-2">
                  {useRecovery
                    ? (t('login.recoveryCodeHint') || 'Saisissez un de vos codes de récupération à usage unique.')
                    : cmsVal(cms, 'sections.form_labels.mfa_hint', t('login.mfaCodeHint') || "Entrez le code de votre application d'authentification.")}
                </p>
                <button
                  type="button"
                  onClick={() => { setUseRecovery(!useRecovery); setMfaCode(''); setError(''); }}
                  className="mt-2 text-xs text-gold-dark hover:text-ink transition-colors underline underline-offset-2"
                  data-testid="mfa-toggle-recovery"
                >
                  {useRecovery
                    ? (t('login.backToTotp') || "Utiliser le code de l'application")
                    : (t('login.useRecoveryCode') || "J'ai perdu l'accès à mon application — utiliser un code de récupération")}
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 mb-3"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-ivory border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {mfaRequired
                    ? cmsVal(cms, 'sections.form_labels.mfa_submit', t('login.submitBtn'))
                    : cmsVal(cms, 'sections.form_labels.submit', t('login.submitBtn'))}
                  <ArrowRight size={18} weight="bold" />
                </>
              )}
            </button>

            <div className="text-center">
              <Link to="/forgot-password" className="text-[13px] text-gold-dark hover:text-ink transition-colors">
                {cmsVal(cms, 'sections.form_labels.forgot_password', t('login.forgotPassword'))}
              </Link>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-stone/60 text-center" data-cms-section="footer_links">
            <p className="text-[13px] text-graphite/80 mb-3">{cmsVal(cms, 'sections.footer_links.no_account_text', t('login.noAccount'))}</p>
            <Link to={localizedPath('requestAccess', lang)} className="inline-flex items-center gap-2 text-[13px] font-medium text-charcoal hover:text-gold-dark transition-colors group" data-testid="login-request-access">
              {cmsVal(cms, 'sections.footer_links.request_access_link', t('login.requestAccessLink'))}
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* ───── RIGHT: DARK VISUAL PANEL ───── */}
      <div
        className="hidden lg:block lg:w-[46%] relative overflow-hidden"
        data-testid="login-side-visual"
        data-cms-section="side_visual"
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `url('${cmsVal(cms, 'sections.side_visual.image_url', '/images/brand/cour_interieure_soir.webp')}')`,
            backgroundPosition: `${cms?.sections?.side_visual?.focalX ?? 50}% ${cms?.sections?.side_visual?.focalY ?? 50}%`,
          }}
          aria-label={cmsVal(cms, 'sections.side_visual.image_alt', '')}
        />
        {/* Dark tech overlay — radial + linear */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(70% 50% at 20% 30%, rgba(158,127,60,0.22) 0%, transparent 60%), linear-gradient(180deg, rgba(14,16,20,0.45) 0%, rgba(14,16,20,0.82) 70%, #0E1014 100%)',
          }}
        />
        {/* Grain overlay */}
        <div
          className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27160%27 height=%27160%27><filter id=%27n%27><feTurbulence baseFrequency=%270.8%27 numOctaves=%272%27 seed=%2710%27/><feColorMatrix values=%270 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/></svg>")',
          }}
        />

        {/* Content */}
        <div className="relative h-full flex flex-col justify-between p-12 lg:p-16 text-ivory">
          {/* Top: small security chip */}
          <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-ivory/10 ring-1 ring-ivory/20 backdrop-blur-sm">
            <ShieldCheck size={14} weight="duotone" className="text-gold" />
            <span className="text-[11px] uppercase tracking-[0.06em] font-medium">
              {lang === 'fr' ? 'Accès sécurisé · MFA' : 'Secure access · MFA'}
            </span>
          </div>

          {/* Bottom: caption */}
          <div className="max-w-md">
            <p className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-gold mb-5">
              <span className="block w-8 h-px bg-gold/70" aria-hidden="true" />
              <Vault size={11} weight="duotone" />
              {cmsVal(cms, 'sections.side_visual.caption_overline', t('login.sideOverline'))}
            </p>
            <h2 className="font-heading text-3xl lg:text-[2.25rem] leading-[1.1] mb-4 text-ivory text-balance">
              {cmsVal(cms, 'sections.side_visual.caption_title', t('login.sideTitle'))}
            </h2>
            <p className="text-ivory/75 text-[15px] leading-relaxed">
              {cmsVal(cms, 'sections.side_visual.caption_subtitle', t('login.sideSubtitle'))}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
